/* G&G Partner Dashboard.
 *
 * Reads /apps/proxy/api/storefront/partners/me and renders it. All money
 * decisions are made on the server; this file only mirrors the same limits so
 * the buttons disable before a pointless round-trip.
 *
 * Conventions taken from the existing portal assets:
 *   - never append shop / logged_in_customer_id / signature by hand, Shopify
 *     injects them and duplicating them breaks the HMAC,
 *   - bail out completely in the theme editor preview,
 *   - localStorage only inside try/catch (not needed here, kept in mind).
 */
(function () {
  'use strict';

  var API = '/apps/proxy/api/storefront/partners';

  function api(path, opts) {
    opts = opts || {};
    var init = { method: opts.method || 'GET', credentials: 'same-origin' };
    if (opts.body) {
      init.headers = { 'Content-Type': 'application/json' };
      init.body = JSON.stringify(opts.body);
    }
    return fetch(API + path, init).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) {
        return { ok: res.ok, status: res.status, data: data };
      });
    });
  }

  function money(n, currency) {
    var v = Number(n) || 0;
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(v);
    } catch (e) {
      return v.toFixed(2) + ' ' + (currency || 'USD');
    }
  }

  // BUSINESS dates are rendered in the SHOP's time zone, never the viewer's.
  //
  // A bare toLocaleDateString() formats in whatever zone the browser sits in,
  // so the same sale showed one day to someone in Kyiv, another in the Shopify
  // admin (which is always shop time) and a third to a buyer in California.
  // The zone travels down in the API payload; until the first response lands we
  // fall back to the viewer's, which is the old behaviour and never worse.
  var SHOP_TZ = null;
  function setShopTz(tz) { if (tz) SHOP_TZ = tz; }
  function dateOpts(o) {
    var out = {};
    for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) out[k] = o[k];
    if (SHOP_TZ) out.timeZone = SHOP_TZ;
    return out;
  }

  function day(iso) {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString(undefined, dateOpts({})); } catch (e) { return ''; }
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function init(root) {
    if (root.getAttribute('data-pd-preview') === '1') return;
    var inner = root.querySelector('.pd-inner');
    if (!inner || inner.getAttribute('data-pd-is-partner') !== '1') return;

    var $ = function (sel) { return inner.querySelector(sel); };
    var els = {
      banner: $('[data-pd-banner]'),
      code: $('[data-pd-code]'),
      copy: $('[data-pd-copy]'),
      available: $('[data-pd-available]'),
      held: $('[data-pd-held]'),
      heldNote: $('[data-pd-held-note]'),
      earned: $('[data-pd-earned]'),
      paid: $('[data-pd-paid]'),
      form: $('[data-pd-form]'),
      paypal: $('[data-pd-paypal]'),
      amount: $('[data-pd-amount]'),
      all: $('[data-pd-all]'),
      submit: $('[data-pd-submit]'),
      hint: $('[data-pd-hint]'),
      pending: $('[data-pd-pending]'),
      pendingAmount: $('[data-pd-pending-amount]'),
      pendingMeta: $('[data-pd-pending-meta]'),
      cancel: $('[data-pd-cancel]'),
      payoutEmpty: $('[data-pd-payout-empty]'),
      referrals: $('[data-pd-referrals]'),
      referralsEmpty: $('[data-pd-referrals-empty]'),
      commissions: $('[data-pd-commissions]'),
      commissionsEmpty: $('[data-pd-commissions-empty]'),
      historyCard: $('[data-pd-history-card]'),
      history: $('[data-pd-history]'),
    };

    var state = { data: null, busy: false };

    function banner(text, kind) {
      if (!els.banner) return;
      if (!text) { els.banner.hidden = true; return; }
      els.banner.textContent = text;
      els.banner.className = 'pd-banner' + (kind ? ' pd-banner--' + kind : '');
      els.banner.hidden = false;
    }

    function show(el, on) { if (el) el.hidden = !on; }

    function renderBalance(d) {
      var c = d.balance.currency;
      els.available.textContent = money(d.balance.available, c);
      els.held.textContent = money(d.balance.held, c);
      els.earned.textContent = money(d.balance.earned, c);
      els.paid.textContent = money(d.balance.paid, c);
      if (els.heldNote) {
        els.heldNote.textContent = d.balance.held > 0
          ? 'Clears ' + d.limits.holdDays + ' days after each payment'
          : '';
      }
    }

    function renderPayout(d) {
      var c = d.balance.currency;
      var pending = (d.payouts || []).filter(function (p) { return p.status === 'pending'; })[0];

      if (pending) {
        // A request is open: the form would only let them double-spend.
        show(els.form, false);
        show(els.payoutEmpty, false);
        show(els.pending, true);
        els.pendingAmount.textContent = money(pending.amount, pending.currency);
        els.pendingMeta.textContent = 'Requested ' + day(pending.createdAt) +
          (pending.payoutEmail ? ' · to ' + pending.payoutEmail : '');
        return;
      }

      show(els.pending, false);

      if (!d.limits.canRequestPayout || d.balance.available <= 0) {
        show(els.form, false);
        show(els.payoutEmpty, true);
        return;
      }

      show(els.payoutEmpty, false);
      show(els.form, true);
      if (els.paypal && !els.paypal.value) els.paypal.value = d.partner.paypalEmail || '';
      if (els.amount && !els.amount.value) els.amount.value = d.balance.available.toFixed(2);
      if (els.amount) els.amount.max = String(d.balance.available);

      var below = d.balance.available < d.limits.minPayout;
      els.submit.disabled = below;
      els.hint.textContent = below
        ? 'Minimum withdrawal is ' + money(d.limits.minPayout, c) + '. You have ' + money(d.balance.available, c) + '.'
        : 'Minimum ' + money(d.limits.minPayout, c) + ' · available ' + money(d.balance.available, c);
    }

    // The project reference. It used to be a FALLBACK for a missing title,
    // which meant it was never on screen: every project has a title. It is the
    // one string a partner and the studio can both quote when something needs
    // checking, so it now leads the row.
    function ref(orderNumber) {
      return orderNumber ? '<span class="pd-ref">' + esc(orderNumber) + '</span>' : '';
    }

    function renderReferrals(d) {
      var rows = d.referrals || [];
      show(els.referralsEmpty, rows.length === 0);
      els.referrals.innerHTML = rows.map(function (r) {
        var pill = r.status === 'qualified'
          ? '<span class="pd-pill pd-pill--ok">earning</span>'
          : r.status === 'rejected'
            ? '<span class="pd-pill pd-pill--no">not eligible</span>'
            : '<span class="pd-pill pd-pill--wait">being checked</span>';

        var meta = [];
        if (r.stage) meta.push(r.stage);
        if (r.ratePercent != null) meta.push('your rate ' + r.ratePercent + '%');
        if (r.isRepeat) meta.push('repeat client');
        meta.push('sent ' + day(r.createdAt));

        // The money line. Either what this project has already paid them, or a
        // plain sentence saying what it is waiting for. Silence on a referral
        // that has earned nothing for weeks reads as a mistake on our side.
        var moneyLine = r.earnedSoFar > 0
          ? '<span class="pd-row-state pd-row-state--ok">' + esc(r.payState) + ' · ' + money(r.earnedSoFar, d.balance.currency) + '</span>'
          : '<span class="pd-row-state">' + esc(r.rejectReason || r.payState || '') + '</span>';

        return '<div class="pd-row">' +
          '<span class="pd-row-main">' +
            '<span class="pd-row-title">' + ref(r.orderNumber) + esc(r.title || 'Project') + '</span>' +
            '<span class="pd-row-meta">' + esc(meta.filter(Boolean).join(' · ')) + '</span>' +
            moneyLine +
          '</span>' + pill +
        '</div>';
      }).join('');
    }

    function renderCommissions(d) {
      var rows = d.commissions || [];
      show(els.commissionsEmpty, rows.length === 0);
      els.commissions.innerHTML = rows.map(function (c) {
        var pill = c.status === 'reversed'
          ? '<span class="pd-pill pd-pill--no">reversed</span>'
          : c.held
            ? '<span class="pd-pill pd-pill--wait">clears ' + esc(day(c.availableFrom)) + '</span>'
            : '<span class="pd-pill pd-pill--ok">available</span>';

        // Spell the sum out. "15% of $1,200 the client paid on 3 July" is
        // checkable; "$180" is something to take on trust.
        var meta = c.ratePercent + '% of ' + money(c.basisAmount, c.currency) +
          ' the client paid · ' + day(c.createdAt);

        return '<div class="pd-row">' +
          '<span class="pd-row-main">' +
            '<span class="pd-row-title">' + ref(c.orderNumber) + esc(c.title || 'Project') + '</span>' +
            '<span class="pd-row-meta">' + esc(meta) + '</span>' +
          '</span>' +
          pill +
          '<span class="pd-row-amount">' + money(c.amount, c.currency) + '</span>' +
        '</div>';
      }).join('');
    }

    function renderHistory(d) {
      // Adjustments belong here too: a partner whose balance moved deserves to
      // see why, in the same place as the payouts.
      var items = (d.payouts || []).filter(function (p) { return p.status === 'paid'; }).map(function (p) {
        return {
          when: p.paidAt || p.createdAt,
          title: 'Paid to you',
          // Where the money actually went. A bare "Payout" row leaves the
          // partner unable to check it against their own PayPal.
          meta: (p.payoutEmail ? 'PayPal ' + p.payoutEmail : 'PayPal') +
            (p.createdAt && p.paidAt ? ' · requested ' + day(p.createdAt) : ''),
          amount: -p.amount,
          currency: p.currency,
        };
      }).concat((d.adjustments || []).map(function (a) {
        return { when: a.createdAt, title: 'Adjustment', meta: a.reason, amount: a.amount, currency: d.balance.currency };
      }));

      items.sort(function (a, b) { return new Date(b.when) - new Date(a.when); });
      show(els.historyCard, items.length > 0);
      if (items.length === 0) return;

      els.history.innerHTML = items.map(function (it) {
        return '<div class="pd-row">' +
          '<span class="pd-row-main">' +
            '<span class="pd-row-title">' + esc(it.title) + '</span>' +
            '<span class="pd-row-meta">' + esc([it.meta, day(it.when)].filter(Boolean).join(' · ')) + '</span>' +
          '</span>' +
          '<span class="pd-row-amount">' + (it.amount > 0 ? '+' : '') + money(it.amount, it.currency) + '</span>' +
        '</div>';
      }).join('');
    }

    // The "How you get paid" steps are the merchant's own words, but three of
    // the values in them belong to the engine: the partner's rate, the holding
    // period and the minimum withdrawal. Editable text with hardcoded numbers
    // is how a page ends up advertising 15% while the engine pays 10, so the
    // numbers are written as {rate} / {hold} / {min} and filled in here from
    // the same payload the balance came from.
    //
    // The original text is kept on the element the first time round: without it
    // a second render would substitute into already-substituted text and the
    // placeholders would be gone.
    function renderHow(d) {
      var rate = d.partner.ratePercent;
      var hold = d.limits.holdDays;

      var vals = {
        '{rate}': rate ? rate + '%' : 'a share',
        '{hold}': hold > 0 ? hold + ' days' : 'straight away',
        '{min}': money(d.limits.minPayout, d.balance.currency),
      };

      var steps = inner.querySelectorAll('[data-pd-how]');
      for (var i = 0; i < steps.length; i++) {
        var el = steps[i];
        if (el.dataset.pdHowTpl == null) el.dataset.pdHowTpl = el.textContent;
        var out = el.dataset.pdHowTpl;
        for (var key in vals) {
          if (Object.prototype.hasOwnProperty.call(vals, key)) {
            out = out.split(key).join(vals[key]);
          }
        }
        // An empty step is a step the merchant deleted, not a blank row.
        var row = el.parentNode;
        if (!out.replace(/\s/g, '')) {
          if (row && row.classList) row.hidden = true;
          continue;
        }
        if (row && row.classList) row.hidden = false;
        el.textContent = out;
      }
    }

    function render(d) {
      state.data = d;
      setShopTz(d && d.timeZone);
      if (els.code) els.code.textContent = d.partner.code || '';
      renderBalance(d);
      renderHow(d);
      renderPayout(d);
      renderReferrals(d);
      renderCommissions(d);
      renderHistory(d);
    }

    function load() {
      return api('/me').then(function (res) {
        if (!res.ok) {
          banner(res.data.error || 'Could not load your dashboard.', 'error');
          return;
        }
        banner('');
        render(res.data);
      }).catch(function () {
        banner('Could not reach the server. Try reloading.', 'error');
      });
    }

    if (els.copy) {
      els.copy.addEventListener('click', function () {
        var base = inner.getAttribute('data-pd-request-url') || '/pages/submit-request';
        var code = state.data && state.data.partner ? state.data.partner.code : '';
        if (!code) return;
        var link = window.location.origin + base + '?ref=' + encodeURIComponent(code);
        var done = function () {
          els.copy.textContent = 'Copied';
          setTimeout(function () { els.copy.textContent = 'Copy link'; }, 1800);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(link).then(done).catch(function () { window.prompt('Your link', link); });
        } else {
          window.prompt('Your link', link);
        }
      });
    }

    if (els.all) {
      els.all.addEventListener('click', function () {
        if (state.data) els.amount.value = state.data.balance.available.toFixed(2);
      });
    }

    if (els.submit) {
      els.submit.addEventListener('click', function () {
        if (state.busy || !state.data) return;
        var email = (els.paypal.value || '').trim();
        var amount = Number(els.amount.value);
        var bal = state.data.balance;
        var lim = state.data.limits;

        // Mirrors the server exactly, so an obvious mistake never becomes a
        // round-trip. The server still re-checks all of it.
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return banner('Enter a valid PayPal address.', 'error');
        if (!(amount > 0)) return banner('Enter an amount.', 'error');
        if (amount < lim.minPayout) return banner('Minimum withdrawal is ' + money(lim.minPayout, bal.currency) + '.', 'error');
        if (amount > bal.available) return banner('That is more than your available balance.', 'error');

        state.busy = true;
        els.submit.disabled = true;
        banner('');
        api('/request-payout', { method: 'POST', body: { payoutEmail: email, amount: amount } })
          .then(function (res) {
            state.busy = false;
            els.submit.disabled = false;
            if (!res.ok) return banner(res.data.error || 'Could not request a payout.', 'error');
            banner('Requested. We pay by hand, so give it a little time.', 'ok');
            return load();
          })
          .catch(function () {
            state.busy = false;
            els.submit.disabled = false;
            banner('Could not reach the server. Try again.', 'error');
          });
      });
    }

    if (els.cancel) {
      els.cancel.addEventListener('click', function () {
        if (state.busy || !state.data) return;
        var pending = (state.data.payouts || []).filter(function (p) { return p.status === 'pending'; })[0];
        if (!pending) return;
        if (!window.confirm('Cancel this payout request? The money goes back to your available balance.')) return;

        state.busy = true;
        els.cancel.disabled = true;
        api('/cancel-payout', { method: 'POST', body: { payoutId: pending.id } })
          .then(function (res) {
            state.busy = false;
            els.cancel.disabled = false;
            if (!res.ok) return banner(res.data.error || 'Could not cancel.', 'error');
            banner('Request cancelled.', 'ok');
            return load();
          })
          .catch(function () {
            state.busy = false;
            els.cancel.disabled = false;
            banner('Could not reach the server. Try again.', 'error');
          });
      });
    }

    load();
  }

  function boot() {
    var roots = document.querySelectorAll('.pd-wrapper');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
